/**
 * Presentation logic for the hosted Studio connection panel and the bounded
 * memory editor. Pure so the states that matter (never paired, offline with a
 * last-seen time, live with a place) can be asserted without rendering.
 */

import type { HostedMemoryScope, HostedStudioStatus } from "./api";

export type StudioPairingTone = "connected" | "idle" | "warning";

export interface StudioPresentation {
  readonly tone: StudioPairingTone;
  readonly label: string;
  readonly detail: string;
  /** True when the user still needs to pair a Studio session. */
  readonly needsPairing: boolean;
}

export function formatRelativeLastSeen(input: {
  readonly lastSeenAt: string | null;
  readonly now: number;
}): string | null {
  if (!input.lastSeenAt) {
    return null;
  }
  const seenAt = Date.parse(input.lastSeenAt);
  if (Number.isNaN(seenAt)) {
    return null;
  }
  const elapsedSeconds = Math.max(0, Math.round((input.now - seenAt) / 1000));
  if (elapsedSeconds < 45) {
    return "just now";
  }
  const minutes = Math.round(elapsedSeconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

export function resolveStudioPresentation(input: {
  readonly status: HostedStudioStatus | null;
  readonly now: number;
}): StudioPresentation {
  if (!input.status) {
    return {
      tone: "idle",
      label: "Checking Studio",
      detail: "Reading the Studio connection state.",
      needsPairing: false,
    };
  }

  const place = input.status.placeName ?? input.status.placeId;
  const lastSeen = formatRelativeLastSeen({ lastSeenAt: input.status.lastSeenAt, now: input.now });

  if (input.status.status === "connected") {
    return {
      tone: "connected",
      label: "Studio connected",
      detail: place ? `Editing ${place}.` : "Waiting for a place to open.",
      needsPairing: false,
    };
  }

  if (input.status.status === "offline") {
    return {
      tone: "warning",
      label: "Studio offline",
      detail: lastSeen
        ? `Last heartbeat ${lastSeen}${place ? ` from ${place}` : ""}.`
        : "The plugin has stopped sending heartbeats.",
      needsPairing: false,
    };
  }

  return {
    tone: "idle",
    label: "Studio not paired",
    detail: "Pair the Studio plugin to let this project read and edit your place.",
    needsPairing: true,
  };
}

export interface MemoryEditorState {
  readonly used: number;
  readonly limit: number;
  readonly remaining: number;
  readonly overCapacity: boolean;
  readonly dirty: boolean;
  readonly canSave: boolean;
  readonly error: string | null;
}

/**
 * A draft with nothing but whitespace in it. The Worker refuses a whitespace-only
 * `add`, and on a document that already has text it would be stored as blanks
 * that read as "memory exists" while saying nothing. Either way the intent is to
 * clear the store, so the editor asks for an empty field instead of sending it.
 */
function isWhitespaceOnly(value: string): boolean {
  return value.length > 0 && value.trim().length === 0;
}

/**
 * Memory writes are explicit, so the editor needs to know three things: how
 * much budget is left, whether the text differs from what the server holds, and
 * whether saving is allowed right now.
 */
export function resolveMemoryEditorState(input: {
  readonly draft: string;
  readonly saved: string;
  readonly limit: number;
  readonly saving: boolean;
}): MemoryEditorState {
  const used = input.draft.length;
  const remaining = input.limit - used;
  const overCapacity = remaining < 0;
  const dirty = input.draft !== input.saved;
  const whitespaceOnly = isWhitespaceOnly(input.draft);
  return {
    used,
    limit: input.limit,
    remaining,
    overCapacity,
    dirty,
    canSave: dirty && !overCapacity && !whitespaceOnly && !input.saving,
    error: overCapacity
      ? `${used - input.limit} character${used - input.limit === 1 ? "" : "s"} over the ${input.limit} character limit. Consolidate before saving.`
      : whitespaceOnly
        ? "Memory cannot be only whitespace. Clear the field to remove it."
        : null,
  };
}

export const MEMORY_SCOPE_LABELS: Readonly<Record<HostedMemoryScope, string>> = {
  user: "User memory",
  project: "Project memory",
};

export const MEMORY_SCOPE_DESCRIPTIONS: Readonly<Record<HostedMemoryScope, string>> = {
  user: "Preferences and communication style. Applied to every project you own.",
  project: "Conventions, architecture, current milestone, and Studio facts for this project.",
};

/**
 * A loaded project memory document together with the project it was read for.
 *
 * The pairing is the point. Memory saves are substring operations keyed on the
 * text the editor is showing, so a document read for one project that is still
 * displayed after the selection moves would send the wrong `match` to the new
 * project: a rejected save at best, and an edit attributed to the wrong project
 * at worst.
 */
export interface ScopedProjectMemory<Document> {
  readonly projectId: string;
  readonly document: Document;
}

/**
 * The project memory document to render, or null when what is loaded belongs to
 * a different project (or no project is selected). Returning null makes the
 * editor show an empty, loading state rather than another project's text.
 */
export function resolveProjectMemoryForSelection<Document>(input: {
  readonly loaded: ScopedProjectMemory<Document> | null;
  readonly selectedProjectId: string | null;
}): Document | null {
  if (!input.selectedProjectId || input.loaded === null) {
    return null;
  }
  return input.loaded.projectId === input.selectedProjectId ? input.loaded.document : null;
}
